import { useAppStore } from '../app';
import { useSDK360Payment } from '@/composables/useSDK360Payment';
import { track } from '@/utils/track';

const QR_LOADING = new URL('@/assets/global/qrcode_load.png', import.meta.url).href;

// 首页支付会话（编排逻辑与确认购买页共用，见 composables/useSDK360Payment）。
// store 是单例，首页同一时刻只有一笔支付，故用模块级单例即可；懒初始化以确保 pinia 已激活。
let paySession: ReturnType<typeof useSDK360Payment> | null = null;
const getPaySession = () => (paySession ??= useSDK360Payment());

export const usePurchaseStore = defineStore('PurchaseStore', {
    state: () => ({
        // 购买、试用、订单等业务状态。
        freeTrialVisible: false,
        buyItem: null,

        // 首页 360 联运支付（真实二维码 + 真实价格）——首页内联二维码区与支付弹窗共用这份数据
        payVisible: false,
        payProduct: null as any,
        payQr: QR_LOADING as string,
        payPrice: '' as string | number,
        payActive: false,           // 是否有正在进行的订单（SDK 内部轮询中），供外部判断是否需要重新下单
        payReset: false,            // 二维码超时后置 true，显示“点击刷新”
        _payLoading: false,         // 防止并发重复拉取二维码
    }),

    getters: {},

    actions: {
        // 所有商品购买入口统一收口到这里处理登录校验。
        buyNow(Item) {
            const appStore = useAppStore();

            // 未登录
            if (!appStore.UserInfo?.token) {
                appStore.openLogin();
                return;
            }

            this.buyItem = Item;
            appStore.goPage('page-purchase');
        },

        closeFreeTrial() {
            this.freeTrialVisible = false;
        },

        // 免费试用弹层在原始逻辑里只负责确认关闭。
        confirmFreeTrial() {
            this.closeFreeTrial();
        },

        // 停止当前订单的 SDK 内部轮询与超时计时
        _stopCurrentOrder() {
            getPaySession().stop();
            this.payActive = false;
        },

        // 检测到当前订单支付成功 → 埋点 + 跳转第二步。
        // statusCallback 由 SDK 按 order_id 回调，只对应当前订单，不会命中历史订单。
        async _handlePaid() {
            const appStore = useAppStore();
            this._stopCurrentOrder();

            // 支付成功埋点（不 await：埋点失败或变慢都不能挡住下面的跳转）
            track('paySuccess');

            appStore.toast('支付成功');
            this.payVisible = false;
            appStore.goPage('page-step2-activation');
        },

        // 创建 360 联运订单 + 生成二维码（编排见 useSDK360Payment，与确认购买页共用）
        async initPay(product) {
            const appStore = useAppStore();
            if (!product || !appStore.UserInfo?.token || this._payLoading) return;

            this._payLoading = true;
            this.payProduct = product;
            this.payReset = false;
            this.payQr = QR_LOADING;
            this.payPrice = '';

            try {
                const ok = await getPaySession().start(product, {
                    onQr: (url) => { this.payQr = url; },
                    onPrice: (price) => { this.payPrice = price; },
                    onPaid: () => this._handlePaid(),
                    onClosed: () => { this._stopCurrentOrder(); this.payReset = true; },
                    onTimeout: () => { this._stopCurrentOrder(); this.payReset = true; },
                    onError: (msg) => { appStore.toast(msg); this.payReset = true; },
                    onStillPending: () => { appStore.toast('尚未检测到支付，若已支付请稍候…'); },
                });
                this.payActive = ok;
            } finally {
                this._payLoading = false;
            }
        },

        // 二维码超时后点击刷新
        refreshPay() {
            if (this.payProduct) this.initPay(this.payProduct);
        },

        // “我已完成支付”按钮：主动查一次 360 订单状态（SDK 本就在后台轮询，这里是即时反馈）
        checkPayNow() {
            const appStore = useAppStore();
            const triggered = getPaySession().queryNow();
            if (!triggered) {
                // 无进行中的订单（未下单 / 已超时 / 已完成）
                appStore.toast('二维码已失效，请点击刷新后重新扫码');
                return;
            }
            appStore.toast('正在确认支付结果…');
        },

        // 打开支付弹窗（登录校验；二维码未就绪/已超时则重新拉取）
        openPay(product) {
            const appStore = useAppStore();
            if (!appStore.UserInfo?.token) {
                appStore.openLogin();
                return;
            }
            const p = product || this.payProduct;
            if (!p) return;
            // 没有在跑的订单、或已超时、或换了商品，才重新下单；避免打断正在进行的支付
            if (this.payReset || this.payProduct !== p || (!this.payActive && !this._payLoading)) {
                this.initPay(p);
            }
            this.payVisible = true;
        },

        closePay() {
            this.payVisible = false;
        },

        // 离开首页时停止轮询并复位
        stopPay() {
            this._stopCurrentOrder();
            this.payVisible = false;
            this.payProduct = null;
            this.payQr = QR_LOADING;
            this.payPrice = '';
            this.payReset = false;
        },
    },
});
